import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Offcanvas } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faUser } from '@fortawesome/pro-light-svg-icons/faUser';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { useSentPitch } from '../../hooks/useSentPitch';
import { outreachMessages } from '../../outreachMessages';
import type { PeopleRow } from '../../outreachTypes';
import { BlockMenu, type BlockMenuActions } from '../BlockMenu';
import { PitchKindCover } from '../PitchKindCover';
import { peopleRowCopy } from '../peopleRowCopy';
import { peopleMessages } from '../peopleMessages';
import { PitchMailCard } from './PitchMailCard';
import { PitchStatusCard } from './PitchStatusCard';
import { PitchWhyCard } from './PitchWhyCard';
import { detailsMessages as messages } from './detailsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the details panel */
import styles from './Details.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The parts of the panel that use the arrow keys themselves: a field, a menu. */
const OWN_ARROWS = 'input, textarea, select, [contenteditable], [role="menu"], .dropdown';

/**
 * ArrowUp / ArrowDown steps to another person only when no other part used the key: not in a
 * field, not in the open block menu (reactstrap handles its arrows and does not stop them), and
 * with no modifier key.
 */
function isStepKey(event: KeyboardEvent): boolean {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
    return false;
  }
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return false;
  }
  return !(event.target instanceof Element && event.target.closest(OWN_ARROWS) !== null);
}

interface PitchDetailsDrawerProps {
  accountId: number;
  campaignUuid: string;
  /** The rows that the arrows step through, in list order. */
  rows: PeopleRow[];
  /** The row whose details show; null closes the panel. */
  row: PeopleRow | null;
  actions: BlockMenuActions;
  disabled: boolean;
  onSelect: (row: PeopleRow) => void;
  onClose: () => void;
}

/**
 * The details of one pitch (design outreach-v2 t2 sent, t3 replied, t4 planned): who, the status
 * card with the timeline, the pitch as the person got it, and why Plug picked them. The footer has
 * "Open in Contacts" and "Never pitch again" (the block menu). The arrows and the ↑/↓ keys step
 * through the list without closing.
 */
const PitchDetailsDrawerComponent: React.FC<PitchDetailsDrawerProps> = ({
  accountId,
  campaignUuid,
  rows,
  row,
  actions,
  disabled,
  onSelect,
  onClose,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { pitch, isLoading, error, retry } = useSentPitch(accountId, campaignUuid, row?.messageUuid ?? null);
  const index = row === null ? -1 : rows.findIndex((item) => item.messageUuid === row.messageUuid);
  const previous = index > 0 ? rows[index - 1] : undefined;
  const next = index >= 0 && index < rows.length - 1 ? rows[index + 1] : undefined;
  const step = useCallback(
    (target: PeopleRow | undefined) => {
      if (target !== undefined) {
        onSelect(target);
      }
    },
    [onSelect]
  );
  useEffect(() => {
    if (row === null) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (!isStepKey(event)) {
        return;
      }
      const target = event.key === 'ArrowUp' ? previous : next;
      if (target !== undefined) {
        event.preventDefault();
        step(target);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, previous, row, step]);

  const body = () => {
    if (row === null) {
      return null;
    }
    const copy = peopleRowCopy(intl, row);
    const contactName = pitch?.contactName ?? null;
    const hasNews = row.result === 'replied' || row.result === 'added';
    return (
      <div className={styles.body} key={row.messageUuid}>
        <div className={styles.id}>
          <PitchKindCover coverUrl={row.coverUrl} kind={row.kind} />
          <div className="flex-grow-1">
            <h2 className={styles.idName}>{copy.title}</h2>
            <p className={styles.idSub}>{copy.sub}</p>
          </div>
        </div>
        {(contactName !== null || (pitch?.toEmail ?? null) !== null) && (
          <div className={styles.contact}>
            <FontAwesomeIcon icon={faUser} className={styles.muted} />
            {contactName !== null && <b className={styles.contactName}>{contactName}</b>}
            {pitch?.toEmail !== null && pitch?.toEmail !== undefined && (
              <span className={styles.contactEmail}>{pitch.toEmail}</span>
            )}
          </div>
        )}
        <PitchStatusCard
          row={row}
          pitch={pitch}
          name={contactName ?? copy.title}
          campaignUuid={campaignUuid}
          disabled={disabled}
          onStopFollowUp={actions.stopFollowUp}
        />
        {isLoading && <LoadingView />}
        {!isLoading && (error !== null || pitch === undefined) && (
          <ErrorView error={error ?? undefined} retry={retry} />
        )}
        {pitch !== undefined && <PitchMailCard pitch={pitch} toName={contactName} folded={hasNews} />}
        <PitchWhyCard row={row} />
      </div>
    );
  };

  const copy = row === null ? null : peopleRowCopy(intl, row);
  return (
    <Offcanvas
      isOpen={row !== null}
      toggle={onClose}
      direction="end"
      className={styles.drawer}
      aria-label={copy === null ? undefined : intl.formatMessage(peopleMessages.drawerTitle, { name: copy.title })}
    >
      <div className={styles.frame} data-testid="sent-pitch-drawer">
        <div className={styles.top}>
          <span className={styles.crumb}>
            {index >= 0 && rows.length > 1
              ? intl.formatMessage(messages.crumbPosition, { index: index + 1, count: rows.length })
              : intl.formatMessage(messages.crumb)}
          </span>
          <div className={styles.topActions}>
            <button
              type="button"
              className={styles.iconButton}
              disabled={previous === undefined}
              onClick={() => step(previous)}
              aria-label={intl.formatMessage(messages.previous)}
              data-testid="details-previous"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              disabled={next === undefined}
              onClick={() => step(next)}
              aria-label={intl.formatMessage(messages.next)}
              data-testid="details-next"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
              aria-label={intl.formatMessage(outreachMessages.close)}
              data-testid="details-close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
        {body()}
        {row !== null && copy !== null && (
          <div className={styles.foot}>
            <SMLButton
              size="sm"
              outline
              omitCap
              leftIcon={faUser}
              onClick={() => router.push(SMLRoutes.outreachContacts, { q: pitch?.contactName ?? copy.title })}
            >
              {intl.formatMessage(peopleMessages.openInContacts)}
            </SMLButton>
            <BlockMenu key={row.messageUuid} row={row} actions={actions} disabled={disabled} />
          </div>
        )}
      </div>
    </Offcanvas>
  );
};

export const PitchDetailsDrawer = TypedMemo(PitchDetailsDrawerComponent);
